<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import _ from 'lodash'
import StreamService from '@/services/StreamService'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import TabGroup from '../../Atoms/TabGroup.vue'
import TabItem from '../../Atoms/TabItem.vue'
import Badge from '../../Badge.vue'
import IngestStatus from './IngestStatus.vue'
import FormButton from '../../Atoms/FormButton.vue'
import IconBase from '../../icon/IconBase.vue'
import IconHelp from '../../icon/IconHelp.vue'
import IconGlobeOutline from '../../icon/IconGlobeOutline.vue'
import Tooltip from '../../Atoms/Tooltip.vue'
import StreamRegion from '../StreamRegion.vue'
import GetRtmpSrtButtons from './GetRtmpSrtButtons.vue'
import TabLinked from './TabLinked.vue'
import TabWebcam from './TabWebcam.vue'
import TabPublish from './TabPublish.vue'
import TabPull from './TabPull.vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Props
const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  streamAlive: {
    type: Boolean
  },
  mediaPulse: {
    type: Object,
    default() { return {} }
  },
  selectedPreviewTab: {
    type: String
  },
  streamSourceType: {
    type: String
  },
  sourceTypes: {
    type: Object
  },
  failoverIngestToggleState: {
    type: Boolean,
    default: false,
  },
  transcoderView: {
    type: Boolean
  },
  isRtmpPullEnabled: {
    type: Boolean,
    default: false
  },
  rmptPullUrlProcessing: {
    type: Boolean,
    default: false
  },
  selectedLinkedSource: {
    type: Object,
    default: null
  },
  streamKeySrt: {
    type: String,
    default: null
  },
  sourceSet: {
    type: Boolean,
    default: false
  },
  failoverIgestActive: {
    type: Boolean,
    default: false
  },
  isPasswordEnabled: {
    type: Boolean,
    default: false
  },
  isAnyBackupStreamLive: {
    type: Boolean,
    default: false
  },
  srtRegionIngestDestination: {
    type: String,
    default: null
  },
  backupStreamAlive: {
    type: Boolean,
    default() { return false }
  },
  primaryStreamAlive: {
    type: Boolean,
    default() { return false }
  },
})

const { notify } = useNotification()
const store = useStore()


const hasFailoverIngestFeature = computed(() => store.getters['User/hasFailoverIngestFeature'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const deploymentRegions = computed(() => store.getters['User/deploymentRegions'])

const emit = defineEmits({
  'stream-updated': (region) => { return { region } },
  'request-url': () => true,
  'update-password-enabled': () => true,
  'set-audio-device': () => true,
  'set-video-device': () => true,
  'pull-urls': () => true,
  'select-preview-tab': () => true
})


const switchRegionProcessing = ref(false)
const failoverIngestToggleProcessing = ref(false)
const deploymentRegion = ref(null)
const rtmps = ref(false)

const setDefaultAudioDevice = (device) => {
  emit('set-audio-device', device)

}

const setDefaultVideoDevice = (device) => {
  emit('set-video-device', device)
}

const setRtmps = (value) => {
  rtmps.value = value
}
onMounted(async () => {
  if (!_.isEmpty(deploymentRegions.value)) {
    const deploymentRegionId = deploymentRegions.value[0]?.regions[0]
    const deploymentRegionInfo = await StreamService.getRegionPull(deploymentRegionId)
    deploymentRegion.value = deploymentRegionInfo
  }
})

const switchStreamRegion = async (region) => {
  if (!region) {
    return
  }

  switchRegionProcessing.value = true
  try {
    await StreamService.switchStreamRegion(props.stream._id, region._id, props.transcoderView)
    emit('stream-updated', region)

    notify({
      group: 'success',
      text: `Region changed to ${region.name}.`
    })
  } catch (e) {
    notify({
      group: 'error',
      text: (e && e.message) || 'could not switch stream region'
    })
  }
  if (region.hostname === 'live.castr.io') {
    failoverIngestToggleProcessing.value = true
  } else failoverIngestToggleProcessing.value = false
  switchRegionProcessing.value = false
}

const selectSourceTab = (sourceTab) => {
  emit('select-source-tab', sourceTab)
}

const classes = (isActive) => `flex h-8 items-center text-sm font-medium rounded-md capitalize justify-center px-2 w-full transition-colors ${isActive ? '!bg-surface-4 !text-white shadow-sm' : 'text-surface-8 hover:bg-surface-3 hover:text-white'}`

const setLinkedSource = () => emit('set-linked-source')
const initSelectedSource = (source) => emit('init-selected-source', source)
const selectedSource = (source) => emit('selected-source', source)

const failoverPrimaryIngest = () => {
  let ingest = _.get(props.stream, 'uiRegion.config.failover.primaryIngest')
  if (!ingest) {
    // ingest = this.stream.uiRegion.hostname.replace('.castr.io', '-primary.castr.io');
    ingest = props.stream?.uiRegion?.hostname || props.stream?.region?.hostname || 'live.castr.io'
  }
  return ingest
}

const failoverBackupIngest = () => {
  if (!hasFailoverIngestFeature.value) {
    return 'xxxxxxxxxxxxxxxxx'
  }
  let ingest = _.get(props.stream, 'uiRegion.config.failover.backupIngest')
  if (!ingest) {
    // ingest = this.stream.uiRegion.hostname.replace('.castr.io', '-backup.castr.io');
    ingest = props.stream?.uiRegion?.hostname || props.stream?.region?.hostname || 'live.castr.io'
  }
  return ingest
}

const getStreamPushUrl = (backupIngest) => {
  const stream = _.cloneDeep(props.stream)
  const region = stream?.uiRegion || stream?.region || {}
  let hostname = region?.hostname || 'live.castr.io'
  const rtmpPort = region?.rtmpPort || 1935
  if (props.transcoderView && !region.rtmpPort) {
    region.rtmpPort = 1935
  }

  if (props.failoverIngestToggleState && !backupIngest && hasFailoverIngestFeature.value) {
    hostname = failoverPrimaryIngest()
  }

  if (props.failoverIngestToggleState && backupIngest) {
    hostname = failoverBackupIngest()
  }
  let pushUrl = `rtmp${rtmps.value ? 's': ''}://${hostname}`
  if ((rtmpPort !== 1935) && (!rtmps.value)) {
    pushUrl += ':' + rtmpPort
  }

  if (rtmps.value) {
    pushUrl += ':1936'
  }

  // @todo
  let prefixPath = _.get(region, 'config.prefixPath')
  prefixPath = prefixPath || '/static'
  prefixPath = _.replace(prefixPath, /^\/\//gi, '/')

  pushUrl += prefixPath


  if (!hasFailoverIngestFeature.value && backupIngest) {
    pushUrl = 'xxxxxxxxxxxxxx'
  }

  if (props.stream.user === '62c4b3398b2e840b11244bfd') {
    pushUrl = 'rtmp://fifa.castr.io/static'
  }

  return pushUrl
}

const getStreamPullUrl = () => {
  // override hostname with region stream is being received
  let hostId = props.mediaPulse?.hostId

  // Check if HLS is enabled in subscription
  const baseSubscription = _.find(addonSubscriptions.value, { category: props.stream.type })
  let hlsEnabled = false

  if (baseSubscription) {
    hlsEnabled = _.get(baseSubscription, 'definitionOverride.hlsEnabled', _.get(baseSubscription, 'package.definition.hlsEnabled', false))
  }

  let server = getStreamPushUrl(null)
  if (props.stream.user === '62c4b3398b2e840b11244bfd') {
    hostId = 'fifa'
  }

  if (hostId) {
    const mappedHostname = `${hostId}.castr.io`
    const defaultHostname = props.stream?.uiRegion?.hostname || props.stream?.region?.hostname
    if (defaultHostname) {
      server = server.replace(defaultHostname, mappedHostname)
    }
    if (props.failoverIngestToggleState && props.hasFailoverIngestFeature) {
      server = `rtmp://${mappedHostname}/static/`
    }
  }

  if (!/\/$/gi.test(server)) {
    server += '/'
  }

  if (hlsEnabled && props.streamSourceType === props.sourceTypes.Pull) {
    if (deploymentRegion.value) {
      const defaultHostname = props.stream?.uiRegion?.hostname || props.stream?.region?.hostname
      if (defaultHostname) {
        server = server.replace(defaultHostname, deploymentRegion.value.pullHostname)
      }
    }
  }

  const pullUrl = server + props.stream.key
  return pullUrl
}

const computeSrtPullUrl = () => {
  let server = props.streamKeySrt
  let hostId = props.mediaPulse?.hostId

  if (props.stream.user === '62c4b3398b2e840b11244bfd') {
    hostId = 'fifa'
  }

  // Check if HLS is enabled in subscription
  const baseSubscription = _.find(addonSubscriptions.value, { category: props.stream.type })
  let hlsEnabled = false
  if (baseSubscription) {
    hlsEnabled = _.get(baseSubscription, 'definitionOverride.hlsEnabled', _.get(baseSubscription, 'package.definition.hlsEnabled', false))
  }

  if (hostId) {
    const mappedHostname = `${hostId}.castr.io`
    server = 'srt://' + mappedHostname + ':9999'
    server += `?streamid=#!::r=${props.stream.key},m=request`
  }
  if (hlsEnabled && props.streamSourceType === props.sourceTypes.Pull) {
    if (deploymentRegion.value) {
      const defaultHostname = props.stream?.uiRegion?.hostname || props.stream?.region?.hostname
      if (defaultHostname) {
        server = server.replace(defaultHostname, deploymentRegion.value.pullHostname)
      }
    }
  }
  return server
}

const requestSRTPullUrl = () => {
  try {
    clipboardCopy(computeSrtPullUrl())
  } catch (e) { }
}

const getStreamUrlAndKey = computed(() => {
  let server = getStreamPushUrl()
  let key = streamKey.value
  const UrlAndKey = server + '/' + key

  return UrlAndKey
})

const streamKey = computed(() => {
  const prefix = props.failoverIgestActive && hasFailoverIngestFeature.value ? '_primary' : ''
  let pushKey = props.stream.key + prefix
  if (props.stream?.config?.password && props.isPasswordEnabled) {
    pushKey += '?password=' + props.stream.config.password
  }
  return pushKey
})

// Pull URLs (for consuming the stream out of Castr). Only meaningful once the
// stream is live (a hostId is assigned). Surfaced in the Playback tab.
const rtmpPullUrl = computed(() => props.mediaPulse?.hostId ? getStreamPullUrl() : '')
const srtPullUrl = computed(() => props.mediaPulse?.hostId ? computeSrtPullUrl() : '')

watch([rtmpPullUrl, srtPullUrl], ([rtmp, srt]) => {
  emit('pull-urls', { rtmp, srt })
}, { immediate: true })

const navigateBackupPage = () => {
  router.push({
    name: 'StreamsManageSettingsBackup',
    params: { streamId: props.stream._id }
  })
}
const onUpdateStream = (pullUrl) => emit('stream-updated', pullUrl)

const requestUrl = (url) => {
  if (url === 'RTMP') clipboardCopy(getStreamPullUrl())
  if (url === 'SRT') requestSRTPullUrl()
}

const goToPlayback = () => emit('select-preview-tab', 'playback')

const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value)
  notify({ group: 'info', text: 'Copied to clipboard' })
}

const updatePasswordEnabled = (value) => {
  console.log(value)
  emit('update-password-enabled', value)
}

</script>
<template>
  <section v-if="failoverIngestToggleState" class="p-3 bg-surface-2">
    <div class="flex items-center justify-between gap-2">
      <h4 class="text-base font-medium">Advanced Encoder Flow Enabled</h4>
      <FormButton class="shrink-0" @click="navigateBackupPage" type="secondary" size="sm">
        Backup Settings
      </FormButton>
    </div>
    <div class="mt-3 flex align-center justify-between">
      <div class="flex justify-between items-center w-full mt-2">
        <h4 class="text-sm text-surface-9">Primary Ingest</h4>
        <IngestStatus :alive="primaryStreamAlive" />
      </div>
    </div>
    <div class="mt-3 flex align-center justify-between">
      <h4 class="flex justify-between align-center w-full">
        <span class="text-sm text-surface-9">Backup Ingest</span>
        <IngestStatus :alive="backupStreamAlive" />
      </h4>
    </div>
    <div class="flex flex-wrap mt-3">
      <div class="w-full">
        <GetRtmpSrtButtons v-if="!transcoderView" @request-url="requestUrl" :stream="stream" :stream-alive="streamAlive"
          :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
          :stream-source-type="streamSourceType" :source-types="sourceTypes" />
      </div>
    </div>
    <div class="flex flex-wrap">
      <div class="w-full">
        <div class="flex items-center justify-center mt-3">
          <a href="https://docs.castr.com/en/articles/4825093-recommended-settings-for-encoders" target="_blank"
            class="inline-flex items-center justify-center text-surface-7 px-0 gap-x-2 text-sm">
            <icon-base>
              <icon-help />
            </icon-base>
            <span>Recommended settings</span>
          </a>
        </div>
      </div>
    </div>
  </section>
  <section v-else class="p-3 bg-surface-2 relative rounded-b-lg">
    <div class="bg-surface-1 rounded-lg p-3">
      <div class="mb-1 flex items-center gap-1.5">
        <icon-base width="15" height="15" width-view-box="24" height-view-box="24" fill="none" class="text-surface-7">
          <icon-globe-outline />
        </icon-base>
        <label class="text-sm text-surface-8 mb-0">Ingest region</label>
        <Tooltip content="Choose the server location closest to your encoder for the best ingest quality.">
          <icon-base class="text-surface-7" fill="none" width="15" height="15"><icon-help /></icon-base>
        </Tooltip>
      </div>
      <StreamRegion :stream="stream" :selector-disabled="streamAlive" filled @switch-stream-region="switchStreamRegion" fullWidth show-alert />
      <p v-if="streamAlive" class="text-tiny text-surface-7 mt-1 mb-0">Stop the stream to change region.</p>
    </div>

    <div class="flex flex-wrap" :class="!transcoderView ? 'mt-3' : ''">
      <div class="w-full" v-if="!transcoderView">
        <label v-if="stream.type !== 'subsecond'" class="block mb-1 text-sm text-surface-8">Source</label>
        <TabGroup v-if="stream.type !== 'subsecond'" class="!bg-surface-1 w-full gap-x-1 grid grid-cols-3">
          <TabItem :customClasses="classes(streamSourceType === sourceTypes.Publish)"
            :isActive="streamSourceType === sourceTypes.Publish" :label="sourceTypes.Publish"
            @click="selectSourceTab(sourceTypes.Publish)" />
          <TabItem :customClasses="classes(streamSourceType === sourceTypes.Pull)"
            :isActive="streamSourceType === sourceTypes.Pull" :label="sourceTypes.Pull"
            @click="selectSourceTab(sourceTypes.Pull)" />
          <TabItem v-if="stream.type !== 'live' && stream.type !== 'restream'"
            :customClasses="classes(streamSourceType === sourceTypes.Webcam)"
            :isActive="streamSourceType === sourceTypes.Webcam" :label="sourceTypes.Webcam"
            @click="selectSourceTab(sourceTypes.Webcam)" />
          <TabItem :customClasses="classes(streamSourceType === sourceTypes.Linked)"
            :isActive="streamSourceType === sourceTypes.Linked"
            v-if="stream.type === 'live' || stream.type === 'restream'" :label="sourceTypes.Linked"
            @click="selectSourceTab(sourceTypes.Linked)" />
        </TabGroup>
      </div>
      <TabLinked v-show="streamSourceType === sourceTypes.Linked" @request-url="requestUrl"
        @set-linked-source="setLinkedSource" @init-selected-source="initSelectedSource"
        @selected-source="selectedSource" :stream="stream" :stream-alive="streamAlive"
        :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
        :transcoder-view="transcoderView" :stream-source-type="streamSourceType" :source-types="sourceTypes"
        :selected-linked-source="selectedLinkedSource" :source-set="sourceSet" />
      <TabWebcam v-show="streamSourceType === sourceTypes.Webcam" @request-url="requestUrl"
        @set-linked-source="setLinkedSource" @init-selected-source="initSelectedSource"
        @selected-source="selectedSource" :stream="stream" :stream-alive="streamAlive"
        :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
        :transcoder-view="transcoderView" :stream-source-type="streamSourceType" :source-types="sourceTypes"
        :selected-linked-source="selectedLinkedSource" :source-set="sourceSet" @set-audio-device="setDefaultAudioDevice"
        @set-video-device="setDefaultVideoDevice" />
      <TabPublish v-show="streamSourceType === sourceTypes.Publish" @request-url="requestUrl"
        @set-linked-source="setLinkedSource" @set-rtmps="setRtmps" @init-selected-source="initSelectedSource"
        @selected-source="selectedSource" :stream="stream" :stream-alive="streamAlive"
        :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
        :transcoder-view="transcoderView" :stream-source-type="streamSourceType" :source-types="sourceTypes"
        :selected-linked-source="selectedLinkedSource" :source-set="sourceSet" :get-stream-push-url="getStreamPushUrl()"
        :get-stream-url-and-key="getStreamUrlAndKey" :srt-region-ingest-destination="srtRegionIngestDestination"
        :is-password-enabled="isPasswordEnabled" :stream-key="streamKey"
        :is-any-backup-stream-live="isAnyBackupStreamLive" :stream-key-srt="streamKeySrt"
        :failover-igest-active="failoverIgestActive"
        @update-password-enabled="updatePasswordEnabled" />
      <TabPull v-show="streamSourceType === sourceTypes.Pull" @request-url="requestUrl"
        @set-linked-source="setLinkedSource" @init-selected-source="initSelectedSource"
        @selected-source="selectedSource" :stream="stream" :stream-alive="streamAlive" @stream-updated="onUpdateStream"
        :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
        :transcoder-view="transcoderView" :stream-source-type="streamSourceType" :source-types="sourceTypes"
        :selected-linked-source="selectedLinkedSource" :source-set="sourceSet" :get-stream-push-url="getStreamPushUrl()"
        :get-stream-url-and-key="getStreamUrlAndKey" :srt-region-ingest-destination="srtRegionIngestDestination"
        :is-password-enabled="isPasswordEnabled" :stream-key="streamKey"
        :is-any-backup-stream-live="isAnyBackupStreamLive" :stream-key-srt="streamKeySrt" />
    </div>
    <p v-if="stream.type === 'live' && !transcoderView" class="text-tiny text-surface-7 mt-3 mb-0">
      Looking for pull URLs? They're in the
      <FormButton custom-classes="text-main font-medium hover:underline" @click="goToPlayback">Playback tab</FormButton>.
    </p>
  </section>
</template>